import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { QuerySummary } from '../../types/viewReports';

interface ReportCardProps {
  id: string;
  title: string;
  description: string;
  date: string;
  defaultExpanded?: boolean;
  children?: React.ReactNode;
  isSelected: boolean;
  onSelectChange: (isSelected: boolean) => void;
  querySummary?: QuerySummary;
  performanceFilter: 'all' | 'green' | 'yellow' | 'red';
  onPerformanceFilterChange: (
    filter: 'all' | 'green' | 'yellow' | 'red'
  ) => void;
  /** When false, hides the row checkbox (PDF download is a single full report). */
  selectable?: boolean;
}

export function ReportCard({
  id,
  title,
  description,
  date,
  defaultExpanded = false,
  children,
  isSelected,
  onSelectChange,
  querySummary,
  performanceFilter,
  onPerformanceFilterChange,
  selectable = true,
}: ReportCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const formattedDate = new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  const handleBadgeClick = (color: 'green' | 'yellow' | 'red') => {
    if (performanceFilter === color) {
      onPerformanceFilterChange('all');
    } else {
      onPerformanceFilterChange(color);
      if (!isExpanded) {
        setIsExpanded(true);
      }
    }
  };

  const PerformanceBadge = ({
    color,
    count,
    tooltip,
  }: {
    color: 'green' | 'yellow' | 'red';
    count: number;
    tooltip: string;
  }) => {
    const isActive = performanceFilter === color;
    const colorClasses = {
      green: `bg-green-100 text-green-700 ${
        isActive ? 'ring-2 ring-green-500 ring-offset-1' : 'hover:bg-green-200'
      }`,
      yellow: `bg-yellow-100 text-yellow-700 ${
        isActive
          ? 'ring-2 ring-yellow-500 ring-offset-1'
          : 'hover:bg-yellow-200'
      }`,
      red: `bg-red-100 text-red-700 ${
        isActive ? 'ring-2 ring-red-500 ring-offset-1' : 'hover:bg-red-200'
      }`,
    };
    const dotClasses = {
      green: 'bg-green-500',
      yellow: 'bg-yellow-500',
      red: 'bg-red-500',
    };

    return (
      <button
        onClick={() => handleBadgeClick(color)}
        className={`group relative inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-all ${colorClasses[color]}`}
        title={tooltip}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full ${dotClasses[color]}`}
        ></span>
        {count}
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
          {tooltip}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></span>
        </span>
      </button>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-solid border-gray-100 overflow-hidden">
      <div className="p-6">
        <div className="flex items-start gap-4 mb-4">
          {selectable ? (
            <input
              type="checkbox"
              id={`checkbox-${id}`}
              checked={isSelected}
              onChange={(e) => onSelectChange(e.target.checked)}
              className="mt-1 w-4 h-4 text-[#1e1b4b] border-gray-300 rounded focus:ring-[#1e1b4b] cursor-pointer"
            />
          ) : (
            <div className="w-4" aria-hidden />
          )}

          <div className="flex-1">
            <div className="flex items-start justify-between mb-4">
              {selectable ? (
                <label
                  htmlFor={`checkbox-${id}`}
                  className="font-bold text-lg text-gray-900 cursor-pointer"
                >
                  {title}
                </label>
              ) : (
                <h3 className="font-bold text-lg text-gray-900">{title}</h3>
              )}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-2 text-sm font-medium text-[#1e1b4b] hover:text-blue-600 transition-colors"
              >
                {isExpanded ? (
                  <>
                    <EyeOff className="w-4 h-4" />
                    Hide Report
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4" />
                    View Report
                  </>
                )}
              </button>
            </div>

            <p className="text-gray-600 text-sm mb-2">{description}</p>

            <div className="flex items-center gap-4 mt-3">
              <span className="text-xs text-gray-400">{formattedDate}</span>

              {querySummary && (
                <div className="flex items-center gap-2">
                  <PerformanceBadge
                    color="green"
                    count={querySummary.green}
                    tooltip="Good: Ratio <= 2x (Click to filter)"
                  />
                  <PerformanceBadge
                    color="yellow"
                    count={querySummary.yellow}
                    tooltip="Warning: 2x < Ratio <= 5x (Click to filter)"
                  />
                  <PerformanceBadge
                    color="red"
                    count={querySummary.red}
                    tooltip="Critical: Ratio > 5x (Click to filter)"
                  />
                  <span className="text-xs text-gray-400">
                    of {querySummary.total} queries
                  </span>
                  {performanceFilter !== 'all' && (
                    <button
                      onClick={() => onPerformanceFilterChange('all')}
                      className="text-xs text-blue-600 hover:text-blue-800 ml-1"
                    >
                      Clear filter
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isExpanded && children && (
        <div className="border-t border-solid border-gray-100 px-6 pb-4">
          <div>{children}</div>
        </div>
      )}
    </div>
  );
}

export default ReportCard;
